import { AccountData } from '@/components/Buyer/BuyerProfile/Account/AccountData';
import { DeliveryAddress } from '@/components/Buyer/BuyerProfile/DeliveryAddress/DeliveryAddress';
import { PaymentAndFiscalData } from '@/components/Buyer/BuyerProfile/PaymentAndFiscalData/PaymentAndFiscalData';
import { CartDirectory } from '@/components/Cart/CartDirectory/CartDirectory';
import { CartsContainer } from '@/components/Cart/CartDirectory/Table/CartOrders';
import { GlobalOrdersContainer } from '@/components/Order/GlobalOrders/GlobalOrders';
import { OrdersDetailContainer } from '@/components/Order/OrdersDetail/OrdersDetail';
import { AdminPrivate } from '@/components/PrivateRoutes/AdminPrivateRoutes';
import { SellerPrivate } from '@/components/PrivateRoutes/SellerPrivateRoutes';
import ProductDirectory from '@/components/Product/ProductDirectory/ProductDirectory';
import SellerProductDirectory from '@/components/Product/SellerProductDirectory/SellerProductDirectory';
import { Products } from '@/components/Product/Table/Products';
import { SellerAccountData } from '@/components/Seller/SellerProfile/Account/SellerAccountData';
import { SellerAddressData } from '@/components/Seller/SellerProfile/Address/SellerAddress';
import { SellerFiscalData } from '@/components/Seller/SellerProfile/FiscalData/SellerFiscalData';
import { SalesAndDelivery } from '@/components/Seller/SellerProfile/SalesAndDelivery/SalesAndDelivery';
import { CartProvider } from '@/context/Cart/CartContext';
import { OrderDetailContextProvider } from '@/context/OrderDetail/OrderDetailContext';
import { ProductState } from '@/hooks/product/catalog/useGetProducts/productState';
import { DashboardUserType } from '@/utils/enums';
import { FC } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import BuyerDirectory from './components/Buyer/BuyerDirectory/BuyerDirectory';
import { BuyerProfile } from './components/Buyer/BuyerProfile/BuyerProfile';
import { LoginContainer } from './components/Login/LoginContainer';
import { OrderDetail } from './components/Order/OrderDetails';
import { DeliveryEvidence } from './components/Order/OrderDetails/DeliveryEvidence/DeliveryEvidence';
import { ProductAndConfigurations } from './components/Order/OrderDetails/ProductAndConfigurations';
import { Orders } from './components/Order/Orders';
import { NewPasswordContainer } from './components/RecoverPassword/NewPassword/NewPasswordContainer';
import { RequestNewPasswordContainer } from './components/RecoverPassword/RequestNewPassword/RequestNewPasswordContainer';
import { RouteFallback } from './components/RouteFallback';
import SellerDirectory from './components/Seller/SellerDirectory/SellerDirectory';
import { SellerProfile } from './components/Seller/SellerProfile/SellerProfile';
import SellerOrdersDirectory from './components/SellerOrders/SellerOrdersDirectory/SellerOrdersDirectory';

const Router: FC = () => {
  return (
    <Routes>
      <Route path="/" element={<Outlet />}>
        {/* Public routes */}
        <Route index element={<RouteFallback />} />
        <Route
          path="/admin/login"
          element={<LoginContainer loginType={DashboardUserType.ADMIN} />}
        />
        <Route
          path="/seller/login"
          element={<LoginContainer loginType={DashboardUserType.SELLER} />}
        />
        <Route
          path="/seller/request-reset-password"
          element={<RequestNewPasswordContainer />}
        />
        <Route
          path="/seller/reset-password"
          element={<NewPasswordContainer />}
        />
        {/* Seller Routes */}
        <Route element={<SellerPrivate />} path="seller">
          <Route index element={<Navigate to="orders" />} />
          <Route path="orders" element={<SellerOrdersDirectory />} />
          <Route
            path="orders/:orderDetailId"
            element={
              <OrderDetailContextProvider>
                <OrderDetail />
              </OrderDetailContextProvider>
            }
          >
            <Route index element={<ProductAndConfigurations />} />
          </Route>
          <Route path="products" element={<SellerProductDirectory />}>
            <Route index element={<Products />} />
            <Route
              path="active"
              element={<Products state={ProductState.Active} />}
            />
            <Route
              path="inactive"
              element={<Products state={ProductState.Inactive} />}
            />
          </Route>
          <Route path="*" element={<>Not Found</>} />
        </Route>
        {/* Admin Routes */}
        <Route element={<AdminPrivate />} path="admin">
          <Route index element={<Navigate to={'seller'} />} />
          {/* Seller */}
          <Route path="seller" element={<SellerDirectory />} />
          <Route path="seller/:sellerId" element={<SellerProfile />}>
            <Route index element={<SellerAccountData />} />
            <Route path={'sales-delivery'} element={<SalesAndDelivery />} />
            <Route path={'fiscal'} element={<SellerFiscalData />} />
            <Route path={'address'} element={<SellerAddressData />} />
          </Route>
          {/* Buyer */}
          <Route path="buyer" element={<BuyerDirectory />} />
          <Route path="buyer/:buyerId" element={<BuyerProfile />}>
            <Route index element={<AccountData />} />
            <Route path="address" element={<DeliveryAddress />} />
            <Route path="billing" element={<PaymentAndFiscalData />} />
          </Route>
          {/* Retailer */}
          <Route path="buyer" element={<BuyerDirectory />} />
          {/* Products */}
          <Route path="products" element={<ProductDirectory />}>
            <Route index element={<Products />} />
            <Route
              path="active"
              element={<Products state={ProductState.Active} />}
            />
            <Route
              path="inactive"
              element={<Products state={ProductState.Inactive} />}
            />
          </Route>
          {/* Ordenes */}
          <Route path="orders" element={<Orders />}>
            <Route index element={<Navigate to="detail" />} />
            <Route path="global" element={<GlobalOrdersContainer />} />
            <Route path="detail" element={<OrdersDetailContainer />} />
            <Route
              path="pending"
              element={<OrdersDetailContainer onlyPendingOrders />}
            />
          </Route>
          <Route
            path="orders/detail/:orderDetailId"
            element={
              <OrderDetailContextProvider>
                <OrderDetail />
              </OrderDetailContextProvider>
            }
          >
            <Route index element={<ProductAndConfigurations />} />
            <Route path="delivery-evidence" element={<DeliveryEvidence />} />
          </Route>
          {/* Borradores de pedidos */}
          <Route
            path="carts"
            element={
              <CartProvider>
                <CartDirectory />
              </CartProvider>
            }
          >
            <Route index element={<CartsContainer />} />
          </Route>
          <Route path="*" element={<>Not Found</>} />
        </Route>
      </Route>
      {/* Redirect rest */}
      <Route path="*" element={<RouteFallback />} />
    </Routes>
  );
};

export default Router;
